import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getRolesForCourse, addCourseRole, deleteCourseRole } from "requests/employeeRoleCourse";
import { getEmployeeRoles } from "requests/employeeRole";
import { DATA_TYPE_DATE } from "typeConstants/dataTypes";
import { MGrid, MSpinner, MCard } from "components/Shared";
import { userHasRight } from "selectors/rights";
import { EMPLOYEE_ROLE_COURSE_ADD, EMPLOYEE_ROLE_COURSE_DELETE } from "typeConstants/rights";
import { defaultHeightStyle } from "styles/sharedStyles";

const colDefs = [
  { name: "Role", dataSource: "employeeRole.name" },
  { name: "Date Created", dataSource: "createDate", dataType: DATA_TYPE_DATE },
].map(c => ({ key: c.name, ...c }));

const editGridColDefs = [{ key: "role", name: "Role", dataSource: "name" }];

const CourseRoles = ({
    courseId,
    canAddEmployeeRoleCourses,
    canDeleteEmployeeRoleCourses,
    cardSx,
  }) => {
  
  const [courseRoles, setCourseRoles] = useState();
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isEditingCourseRoles, setIsEditingCourseRoles] = useState(false);
  const [selectedGridIndex, setSelectedGridIndex] = useState();
  const [roles, setRoles] = useState();

  const mounted = useRef(true);
  const instance = useRef({ activeWebCallCount: 0 });

  const resetState = () => {
    setSelectedRoles([]);
    setSelectedGridIndex(null);
    setCourseRoles(null);
    setIsEditingCourseRoles(false);
  };

  useEffect(() => {
    getEmployeeRoles(setRoles, mounted);
    return () => mounted.current = false;
  }, []);

  const getCourseRolesWrapped = () => getRolesForCourse(courseId, setCourseRoles, mounted);

  useEffect(() => {
    resetState();
    getCourseRolesWrapped();
  }, [courseId]);
  
  const onCourseRoleClicked = index => {
    setSelectedGridIndex(index);
  };

  const onEditClicked = () => {
    setIsEditingCourseRoles(true);
    setSelectedRoles(courseRoles.map(cr => roles.findIndex(r => r.id === cr.employeeRole.id)));
  };

  const getSuccessCallbackForEdit = () => {
    instance.current.activeWebCallCount++;
    return () => {
      instance.current.activeWebCallCount--;
      if (instance.current.activeWebCallCount === 0) {
        getCourseRolesWrapped();
      }
    };
  };

  const onEditFinished = () => {
    if (instance.current.activeWebCallCount !== 0) return;
    let previouslySelectedRoleIds = courseRoles.map(cr => cr.employeeRole.id);
    let selectedRoleIds = selectedRoles.map(i => roles[i].id);

    resetState();

    // find all the previous selected role ids that arent in the selected role ids, these need to be removed
    previouslySelectedRoleIds
      .filter(pr => !selectedRoleIds.some(sr => sr === pr))
      .forEach(id => deleteCourseRole(courseRoles.find(cr => cr.employeeRole.id === id).id, getSuccessCallbackForEdit(), mounted));

    // find all the selected role ids that werent previously selected, these need to be added
    selectedRoleIds
      .filter(sr => !previouslySelectedRoleIds.some(pr => pr === sr))
      .forEach(id => addCourseRole({ courseId: courseId, employeeRoleId: id }, getSuccessCallbackForEdit(), mounted));
  };

  const onEditCancelled = () => {
    setIsEditingCourseRoles(false);
  };

  if (!courseRoles) return <MSpinner />;
  
  let cardActions = [
    !isEditingCourseRoles && canAddEmployeeRoleCourses && canDeleteEmployeeRoleCourses && { icon: "edit", onClick: onEditClicked },
    isEditingCourseRoles && { icon: "check", helpText: "Apply Changes", onClick: onEditFinished },
    isEditingCourseRoles && { icon: "close", helpText: "Cancel Editing", onClick: onEditCancelled },
  ].filter(c => c);

  const roleSelector = () => 
    roles ?
      <MGrid
        columnDefinitions={editGridColDefs}
        data={roles}
        allowRowSelection
        onRowSelectionChanged={roles => setSelectedRoles(roles)}
        selectedRows={selectedRoles}
      /> :
      <MSpinner/>;

  return (
    <MCard
      title="Course Roles"
      className="course-roles"
      actions={cardActions}
      sx={{ ...defaultHeightStyle, ...cardSx }}
    >
      {isEditingCourseRoles ?
        roleSelector() :
        <MGrid
          columnDefinitions={colDefs}
          data={courseRoles}
          disableFilters
          onRowClick={i => onCourseRoleClicked(i)}
          rowLastClicked={selectedGridIndex}
        />}
    </MCard>
  );
};

CourseRoles.propTypes = {
  courseId: PropTypes.number.isRequired,
  canAddEmployeeRoleCourses: PropTypes.bool.isRequired,
  canDeleteEmployeeRoleCourses: PropTypes.bool.isRequired,
  cardSx: PropTypes.object,
};

const mapStateToProps = state => ({
  canAddEmployeeRoleCourses: userHasRight(EMPLOYEE_ROLE_COURSE_ADD)(state),
  canDeleteEmployeeRoleCourses: userHasRight(EMPLOYEE_ROLE_COURSE_DELETE)(state),
});

export default connect(mapStateToProps)(CourseRoles);
